import { useEffect, useMemo, useState } from "react";
import App from "./App";
import AppErrorBoundary from "./components/AppErrorBoundary";
import argusLogo from "./assets/argus-logo-text-2.png";

type AuthUser = {
  id: string;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
};

type AuthState = "loading" | "authenticated" | "unauthenticated";

async function readJsonSafe(response: Response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function getCookie(name: string) {
  const match = document.cookie.split("; ").find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split("=")[1] ?? "") : null;
}

export default function AppShell() {
  const [authState, setAuthState] = useState<AuthState>("loading");
  const [user, setUser] = useState<AuthUser | null>(null);
  const [error, setError] = useState<string | null>(null);

  const displayName = useMemo(() => {
    if (!user) return "";
    const name = [user.firstName, user.lastName].filter(Boolean).join(" ");
    if (name) return name;
    if (user.email) return user.email;
    return "User";
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    setAuthState("loading");
    setError(null);

    fetch("/api/auth/me", { credentials: "include" })
      .then(async (res) => {
        if (res.status === 401) return null;
        if (!res.ok) {
          const body = await res.text();
          throw new Error(body || "Auth check failed");
        }
        return res.json();
      })
      .then(async (data) => {
        if (cancelled) return;
        if (data?.user) {
          const csrfToken = getCookie("csrfToken");
          const tokenResponse = await fetch("/api/auth/convex-token", {
            method: "POST",
            credentials: "include",
            headers: {
              ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
            },
          });
          const tokenPayload = await readJsonSafe(tokenResponse);
          if (!tokenResponse.ok || typeof tokenPayload?.accessToken !== "string" || !tokenPayload.accessToken) {
            const tokenError =
              typeof tokenPayload?.error === "string"
                ? tokenPayload.error
                : "Unable to establish secure data session";
            throw new Error(tokenError);
          }

          if (cancelled) return;
          setUser(data.user);
          setAuthState("authenticated");
        } else {
          setUser(null);
          setAuthState("unauthenticated");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || "Auth check failed");
        setUser(null);
        setAuthState("unauthenticated");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSignIn = () => {
    const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    window.location.assign(`/api/auth/login?returnTo=${encodeURIComponent(returnTo)}`);
  };

  const handleSignOut = async () => {
    const csrfToken = getCookie("csrfToken");
    try {
      const response = await fetch(`/api/auth/logout?returnTo=${encodeURIComponent("/")}`, {
        method: "POST",
        credentials: "include",
        headers: {
          ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
        },
      });
      const payload = await response.json().catch(() => null);
      const logoutUrl = typeof payload?.logoutUrl === "string" ? payload.logoutUrl : "/";
      window.location.assign(logoutUrl);
    } catch {
      window.location.assign("/");
    }
  };

  const authBrand = (
    <div className="auth-brand-stack">
      <img src={argusLogo} alt="Argus logo" className="auth-logo auth-logo-large" />
    </div>
  );

  if (authState === "loading") {
    return (
      <div className="auth-screen">
        <div className="auth-background" aria-hidden="true" />
        <div className="auth-shell">
          {authBrand}
          <div className="auth-card auth-login-card">
            <div className="auth-loading-row">
              <span className="auth-spinner" aria-hidden="true" />
              <span>Validating secure session...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (authState === "unauthenticated") {
    return (
      <div className="auth-screen">
        <div className="auth-background" aria-hidden="true" />
        <div className="auth-shell">
          {authBrand}
          <div className="auth-login-inline">
            {error ? <div className="auth-error">{error}</div> : null}
            <button type="button" className="auth-primary-btn" onClick={handleSignIn}>
              Sign in
            </button>
            <div className="auth-footnote">Authorized users only.</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AppErrorBoundary
      onSignIn={handleSignIn}
      onReload={() => {
        window.location.reload();
      }}
    >
      <App
        sessionDisplayName={displayName}
        sessionEmail={user?.email || ""}
        onSignOut={handleSignOut}
      />
    </AppErrorBoundary>
  );
}
